// Treatment strings and TreatmentId UUIDs are join keys against stored rows;
// value-fidelity.test.ts pins them character for character.
// This ControlSource (why one tap was audio-only) is not ./study's
// ControlSource (how a word became a study control word).
import type { ResponseId, ActionId } from "./fluency";

// UnitBridging: shows the target beside already-mastered sight words that share
// its units (morphemes, onsets, rimes, blends) to surface orthographic patterns.
export const Treatment = {
  None: "no_intervention",
  AidedReading: "aided_reading_v1.0",
  DrawerMatch: "word_match_drawer_v1.0",
  WordReadingFluency: "word_reading_fluency_v1.0",
  PopoverMatch: "popover_match_v1.0",
  DynamicKeying: "dynamic_keying_v1.0",
  QwertyKeying: "qwerty_keying_v1.0",
  UnitBridging: "unit_bridging_v1.0",
  SentenceDrill: "sentence_drill_v1.0",
} as const;
export type Treatment = (typeof Treatment)[keyof typeof Treatment];

// What a personalised reading may set on a word; the CDN JSON never carries a
// treatment, so a word without one falls back to the arm and CDN rules.
export const SERVED_WORD_TREATMENTS = [
  Treatment.AidedReading,
  Treatment.DrawerMatch,
  Treatment.UnitBridging,
] as const;
export type ServedWordTreatment = (typeof SERVED_WORD_TREATMENTS)[number];

export const TreatmentId = {
  AidedReading: "1a70ac1a-eede-4f8b-90ad-95e17ffdb2c8",
  DrawerMatch: "b81c3df9-8e55-42f4-8577-ab3755469741",
  WordReadingFluency: "1e116158-5abb-4385-9e49-41f9965f7cbb",
  PopoverMatch: "41f0b448-c7ff-45cd-88ac-0013fb22bc21",
  DynamicKeying: "37ceb4d9-f7c3-4205-9e11-bb8834a0f528",
  QwertyKeying: "80f14f9c-89c5-4dd9-bcef-910956a0114a",
  UnitBridging: "db687397-9e64-4534-9024-22895fd839c4",
  SentenceDrill: "a3f7c291-5e84-4b6a-9d12-8c4e6f3a1b50",
} as const;
export type TreatmentId = (typeof TreatmentId)[keyof typeof TreatmentId];

export const TreatmentTypeToId = {
  [Treatment.None]: null,
  [Treatment.AidedReading]: TreatmentId.AidedReading,
  [Treatment.DrawerMatch]: TreatmentId.DrawerMatch,
  [Treatment.WordReadingFluency]: TreatmentId.WordReadingFluency,
  [Treatment.PopoverMatch]: TreatmentId.PopoverMatch,
  [Treatment.DynamicKeying]: TreatmentId.DynamicKeying,
  [Treatment.QwertyKeying]: TreatmentId.QwertyKeying,
  [Treatment.UnitBridging]: TreatmentId.UnitBridging,
  [Treatment.SentenceDrill]: TreatmentId.SentenceDrill,
} as const;

export const TreatmentIdToTreatment: Record<
  TreatmentId,
  Exclude<Treatment, typeof Treatment.None>
> = {
  [TreatmentId.AidedReading]: Treatment.AidedReading,
  [TreatmentId.DrawerMatch]: Treatment.DrawerMatch,
  [TreatmentId.WordReadingFluency]: Treatment.WordReadingFluency,
  [TreatmentId.PopoverMatch]: Treatment.PopoverMatch,
  [TreatmentId.DynamicKeying]: Treatment.DynamicKeying,
  [TreatmentId.QwertyKeying]: Treatment.QwertyKeying,
  [TreatmentId.UnitBridging]: Treatment.UnitBridging,
  [TreatmentId.SentenceDrill]: Treatment.SentenceDrill,
};

// An open set: a new kind is meant to break exhaustive consumers rather than
// be silently dropped, so do not add a catch-all variant.
export type LearningPosition =
  | {
      kind: "reading-token";
      readingId: string;
      sentenceId: string;
      tokenIdx: number;
    }
  | {
      kind: "batch-tile";
      jmdictSeq: string;
      rankInBatch: number;
      pos: string;
    }
  | {
      kind: "drill-sentence";
      sentenceId: string;
      readingId: string;
      direction: "ja_to_en" | "en_to_ja";
    };

// `no_choices` is a treatment word with no CDN choices, so it fell back to
// audio only; it is not a study control word and analysts exclude it from arm
// comparisons. `assignment` is a word whose per-reader study arm is control;
// `cdn` is the reading's own flag, used only for a reader with no arms.
// `fallback` is a reading opened all AidedReading because neither the
// personalised route nor a cached copy of it was reachable; it shows no arm.
export const CONTROL_SOURCES = [
  "cdn",
  "intake",
  "no_choices",
  "assignment",
  "fallback",
] as const;
export type ControlSource = (typeof CONTROL_SOURCES)[number];

// `completed` means the intervention was delivered: the drawer match was made,
// or the playback sequence finished (a clip failing with no fallback still
// ends it). Anything that ends the interaction first is `abandoned`.
export const INTERACTION_COMPLETIONS = ["completed", "abandoned"] as const;
export type InteractionCompletion = (typeof INTERACTION_COMPLETIONS)[number];

// Convex mutations, not this shape, convert to snake_case + bigint for storage.
// targetFluencyRecord: the history before this engagement.
// precedingFluencyRecord: skipped-fluently targets plus this one, never empty.
// Populate `position` or the legacy `wordInReading`, not both.
export interface LearningRecordContext {
  word?: string;
  response: ResponseId;
  prediction?: {
    responseId: ResponseId;
    confidence?: number;
  };
  actionId: ActionId;
  treatmentId: TreatmentId;
  readerId: string;
  sessionId: string;
  sessionStartTime?: number;
  createdAt: number;
  pageStartTime?: number;
  readingRate?: number;
  targetFluencyRecord?: ResponseId[];
  precedingFluencyRecord?: ResponseId[];
  choicesPresented?: string[];
  incorrectMatches?: string[];
  // More wrong picks than the server stores: incorrectMatches keeps the
  // most recent ones. Absent, never false.
  incorrectMatchesTruncated?: true;
  utcOffset?: number;
  wordInReading?: {
    index: number;
    readingId: string;
  };
  position?: LearningPosition;
  controlSource?: ControlSource;
  completion?: InteractionCompletion;
  // `createdAt` is the tap; this is when the interaction ended.
  completedAt?: number;
  // The word was already passed over and credited Recognition.
  lookBack?: boolean;
  // Same help event as the previous tap, so no new fluency response was written.
  repeatTap?: boolean;
  playbackFailed?: boolean;
  // The actionId was computed before the reader's history reached the
  // device, so it may disagree with the server's. Absent, never false.
  historyCold?: true;
  // The bridge set the served reading was planned under, on every record
  // from a personalised reading whatever its treatment, so their shared
  // columns and scores can be recomputed; absent on a CDN reading's taps.
  bridgeSetVersion?: string;
  // When the personalised route served that reading, by Convex's clock (its
  // x-personal-reading-served-at header), so the commit can tell a copy
  // served before a word's UnitBridging flip from one served after it.
  servedAt?: number;
  // Re-sent by the Dexie v3 upgrade without a clientRecordId; the server
  // dedupes it on (readerId, word, createdAt, treatmentId, actionId).
  recovered?: boolean;
}

export interface LearningRecord {
  version: "0.1.0";
  readerId: string;
  sessionId: string;
  // Absent on older records: treat undefined as unknown, not zero.
  sessionStartTime?: Date;
  records: LearningRecordEntry[];
}

export interface LearningRecordEntry {
  createdAt: Date;
  wordAsString: string;
  prediction?: ResponseId;
  response: ResponseId;
  actionId: ActionId;
  treatmentId: TreatmentId;
  context?: LearningRecordContext;
  // Convex dedupes re-sends on it; absent on records from older app versions.
  clientRecordId?: string;
  // The word as the child saw it, before extractNormalizedWord folded it.
  surface?: string;
  // The fluency model release a `prediction` was made with (Q8).
  modelRelease?: string;
  vocabRelease?: string;
}
